/**
 * @file assessmentListDisplay.ts
 * @brief Shared list row labels for Medical Assessment (web + mobile parity).
 *
 * @author DES
 * @date 02-09-2026
 */

import type { MedicalAssessment } from "./schemas";

export function assessmentClaimantName(item: MedicalAssessment): string {
  const sheetName = item.caseHub?.identitySheet?.["Claimant's full name"]?.trim();
  if (sheetName) return sheetName;
  const c = item.caseHub?.claimant;
  const name = [c?.title, c?.forenames, c?.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || item.title || "Medical assessment";
}

export function assessmentReference(item: MedicalAssessment): string | undefined {
  const ref = item.caseHub?.refs?.uniqueReferenceNumber?.trim();
  return ref || undefined;
}

export function hasCaseHubAttached(item: MedicalAssessment): boolean {
  return Boolean(
    item.caseHub?.claimant?.surname ||
      item.caseHub?.refs?.uniqueReferenceNumber,
  );
}

export function assessmentStatusHints(item: MedicalAssessment): string {
  const parts: string[] = [item.status];
  if (item.status === "intake_pending") {
    parts.push("From clinic software");
    return parts.join(" · ");
  }
  if (!hasCaseHubAttached(item)) {
    parts.push("Needs client info");
  } else {
    parts.push("Ready to record");
  }
  if (item.sessionId) {
    parts.push("Recorded");
  }
  return parts.join(" · ");
}

/** Partner push label for inbox rows. */
export function intakeSourceLabel(item: MedicalAssessment): string {
  const meta = item.metadata;
  const fromMeta =
    typeof meta?.sourceSystem === "string" ? meta.sourceSystem : undefined;
  const fromCaseHub =
    item.caseHub?.intake === true && typeof item.caseHub?.source === "string"
      ? item.caseHub.source
      : undefined;
  const raw = fromMeta ?? fromCaseHub ?? "partner";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Secondary line under claimant name — usually MEDYX / agency ref. */
export function assessmentListSubtitle(item: MedicalAssessment): string | undefined {
  const ref = assessmentReference(item);
  if (ref) return ref;
  const name = assessmentClaimantName(item);
  if (item.title && item.title !== name) return item.title;
  return undefined;
}
